import { computeCampWeekSessionDates, formatDateToFormYmd } from './lessonDates';

/** Minimal enrollment row shape for camp per-day counting. */
export interface CampEnrollmentCountRow {
  studentId: string;
  sessionDateYmd?: string;
  bookingScope?: 'term' | 'session' | 'package';
}

export function resolveCampSessionDatesForCapacity(
  lesson: {
    campSessionDates?: string[];
    startDate?: unknown;
    daysOfWeek?: number[];
  },
  explicitSessionDates?: string[]
): string[] {
  if (lesson.campSessionDates?.length) {
    return [...lesson.campSessionDates]
      .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
      .sort();
  }
  if (explicitSessionDates?.length) {
    return [...explicitSessionDates]
      .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
      .sort();
  }
  const startYmd = formatDateToFormYmd(lesson.startDate as Parameters<typeof formatDateToFormYmd>[0]);
  if (startYmd && lesson.daysOfWeek?.length) {
    return computeCampWeekSessionDates(startYmd, lesson.daysOfWeek);
  }
  return [];
}

function isFullWeekCampEnrollment(row: CampEnrollmentCountRow): boolean {
  if (row.sessionDateYmd) return false;
  return row.bookingScope === 'term' || row.bookingScope === 'package';
}

/** Whether an enrollment row applies to a specific camp day roster. */
export function isEnrollmentOnCampDay(
  row: CampEnrollmentCountRow,
  sessionDateYmd: string
): boolean {
  if (isFullWeekCampEnrollment(row)) return true;
  return row.sessionDateYmd === sessionDateYmd;
}

/** Group enrollment rows under each camp session date (full-week rows appear on every day). */
export function groupCampEnrollmentsByDay<T extends CampEnrollmentCountRow>(
  sessionDates: string[],
  enrollments: T[]
): Array<{ sessionDateYmd: string; enrollments: T[] }> {
  return sessionDates.map((sessionDateYmd) => ({
    sessionDateYmd,
    enrollments: enrollments.filter((row) => isEnrollmentOnCampDay(row, sessionDateYmd)),
  }));
}

/**
 * Count unique students enrolled on each camp day.
 * Full-week bookings count toward every session date; daily bookings count on their day only.
 */
export function computeCampEnrollmentByDay(
  sessionDates: string[],
  enrollments: CampEnrollmentCountRow[]
): Record<string, number> {
  const studentsByDay = new Map<string, Set<string>>();
  for (const date of sessionDates) {
    studentsByDay.set(date, new Set());
  }

  for (const row of enrollments) {
    if (isFullWeekCampEnrollment(row)) {
      for (const date of sessionDates) {
        studentsByDay.get(date)!.add(row.studentId);
      }
    } else if (row.sessionDateYmd && studentsByDay.has(row.sessionDateYmd)) {
      studentsByDay.get(row.sessionDateYmd)!.add(row.studentId);
    }
  }

  const counts: Record<string, number> = {};
  for (const date of sessionDates) {
    counts[date] = studentsByDay.get(date)?.size ?? 0;
  }
  return counts;
}

export function countUniqueCampStudents(enrollments: CampEnrollmentCountRow[]): number {
  return new Set(enrollments.map((row) => row.studentId)).size;
}

/** True when every camp day has reached max capacity. */
export function isCampFullyBookedByDay(
  sessionDates: string[],
  enrollmentByDay: Record<string, number>,
  maxCapacity: number
): boolean {
  if (maxCapacity <= 0 || maxCapacity === -1 || sessionDates.length === 0) return false;
  return sessionDates.every((date) => (enrollmentByDay[date] ?? 0) >= maxCapacity);
}

/** True when selected camp days are all at capacity. */
export function areCampDaysFullyBooked(
  selectedDates: string[],
  enrollmentByDay: Record<string, number>,
  maxCapacity: number
): boolean {
  if (maxCapacity <= 0 || maxCapacity === -1 || selectedDates.length === 0) return false;
  return selectedDates.every((date) => (enrollmentByDay[date] ?? 0) >= maxCapacity);
}
